import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type StudieKvalitetsregister = {
    id: number;
    namn: string;
};

export type StudieKvalitetsregisterBackend = StudieKvalitetsregister & {
    studieregister_display: StudieKvalitetsregister;
};

type State = BaseState & {
    studieKvalitetsregister: Map<StudieKvalitetsregister["id"], StudieKvalitetsregister>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    studieKvalitetsregisterById: (data: { id: number }) => StudieKvalitetsregister | null;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        studieKvalitetsregister: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "studieKvalitetsregister";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Returnerar alla studieKvalitetsregister-objekt som array
         */
        studieKvalitetsregister: (state: State) => [...state.studieKvalitetsregister.values()],
        /**
         * Subset-getter för studieKvalitetsregister
         */
        studieKvalitetsregisterSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "studieKvalitetsregister",
            }),
        /**
         * Hämta ett enskilt studieKvalitetsregister-objekt på id
         */
        studieKvalitetsregisterById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }
                return state.studieKvalitetsregister.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Lägg till nya studieKvalitetsregister-objekt i store
         */
        addStudieKvalitetsregisterMutation: (
            state: State,
            {
                offset,
                subsetName = "default",
                studieKvalitetsregister = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                studieKvalitetsregister: StudieKvalitetsregister[];
            }
        ) =>
            subsetSetter<State, StudieKvalitetsregister>({
                offset,
                subsetName,
                state,
                collectionName: "studieKvalitetsregister",
                items: studieKvalitetsregister,
            }),
        /**
         * Uppdatera studieKvalitetsregister-objekt i store
         */
        updateStudieKvalitetsregisterMutation: (
            state: State,
            {
                studieKvalitetsregister,
                offset,
                subsetName,
            }: {
                studieKvalitetsregister: StudieKvalitetsregister[];
                offset: number;
                subsetName: keyof Subsets;
            }
        ) =>
            subsetUpdateSetter<State, StudieKvalitetsregister>({
                offset,
                state,
                collectionName: "studieKvalitetsregister",
                items: studieKvalitetsregister,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        async getStudieKvalitetsregisterById(
            { getters, commit, state }: { getters: any; commit: Commit; state: State },
            { id }: { id: number }
        ) {
            const found = getters.studieKvalitetsregisterById({ id });
            if (found || !id) return found;
            try {
                const { data } = await klinikenApi.get<{ studieKvalitetsregister: StudieKvalitetsregisterBackend }>(
                    `operation/studieKvalitetsregister/${id}/`,
                    getConfig({})
                );
                if (data.studieKvalitetsregister) {
                    commit("addStudieKvalitetsregisterMutation", {
                        offset: 0,
                        studieKvalitetsregister: [data.studieKvalitetsregister],
                        subsetName: id,
                    });
                    return data.studieKvalitetsregister;
                }
                return null;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async fetchStudieKvalitetsregister(
            { commit, state }: { commit: Commit; state: State },
            { limit = 2, page = 1, filterParams }: { limit?: number; page?: number; filterParams?: any }
        ) {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<
                    State,
                    StudieKvalitetsregisterBackend
                >({
                    filterParams,
                    state,
                    limit,
                    page,
                    collectionName: "studieKvalitetsregister",
                    apiUrl: "operation/studie_kvalitetsregister/",
                });
                if (!results) {
                    return { subsetName, total, nextPage };
                }
                commit("addStudieKvalitetsregisterMutation", {
                    offset,
                    subsetName,
                    studieKvalitetsregister: results,
                });
                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async createStudieKvalitetsregister(
            { commit, state }: { commit: Commit; state: State },
            { studieKvalitetsregister }: { studieKvalitetsregister: StudieKvalitetsregister }
        ) {
            try {
                const response = await klinikenApi.post(
                    "operation/studie_kvalitetsregister/",
                    studieKvalitetsregister,
                    getConfig({})
                );
                commit("addStudieKvalitetsregisterMutation", {
                    offset: Object.keys(state.subsets?.default?.ids || {}).length,
                    // subsetName: "newlyCreated",
                    studieKvalitetsregister: [response.data],
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async updateStudieKvalitetsregister(
            { commit, state }: { commit: Commit; state: State },
            { studieKvalitetsregister }: { studieKvalitetsregister: StudieKvalitetsregister }
        ) {
            try {
                const response = await klinikenApi.put(
                    `operation/studie_kvalitetsregister/${studieKvalitetsregister.id}/`,
                    studieKvalitetsregister,
                    getConfig({})
                );
                commit("updateStudieKvalitetsregisterMutation", {
                    // subsetName: "newlyUpdated",
                    studieKvalitetsregister: [response.data],
                    offset: Object.keys(state.subsets?.default?.ids || {}).length,
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
